import React from 'react';
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  CircularProgress,
  FormControlLabel,
  Grid,
  Switch,
  Typography,
} from '@mui/material';
import { WarningAmber } from '@mui/icons-material';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';
import { apiClient } from '../../services/api';
import { diagnosticsHealthColor } from '../../utils/diagnosticsHealth';
import { browserMonitoringOverviewLabel, browserMonitoringHealthLevel } from '../../utils/monitoringPresentation';

function SummaryCard({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <Card variant="outlined" sx={{ height: '100%' }}>
      <CardContent>
        <Typography variant="overline" color="text.secondary">{title}</Typography>
        <Box mt={1}>{children}</Box>
      </CardContent>
    </Card>
  );
}

function Row({ label, value }: { label: string; value: React.ReactNode }) {
  return (
    <Box display="flex" justifyContent="space-between" gap={2} mb={0.75}>
      <Typography variant="body2" color="text.secondary">{label}</Typography>
      <Typography variant="body2" textAlign="right">{value || '—'}</Typography>
    </Box>
  );
}

interface ComputerOverviewPanelProps {
  deviceId: string;
}

const ComputerOverviewPanel: React.FC<ComputerOverviewPanelProps> = ({
  deviceId,
}) => {
  const navigate = useNavigate();
  const queryClient = useQueryClient();

  const { data: inventoryData, isLoading: inventoryLoading } = useQuery({
    queryKey: ['monitoringDevicesInventory'],
    queryFn: () => apiClient.monitoring.listDevices(),
    refetchInterval: 30000,
  });

  const { data: diagnosticsData, isLoading: diagnosticsLoading } = useQuery({
    queryKey: ['deviceDiagnosticsBrief', deviceId],
    queryFn: () => apiClient.monitoring.getDeviceDiagnostics(deviceId),
    enabled: !!deviceId,
    retry: false,
  });

  const devices = inventoryData?.data?.data || [];
  const device = devices.find((d: any) => d.id === deviceId || d.device_id === deviceId);
  const diagnostics = diagnosticsData?.data?.data;
  // Prefer WP2 Phase G runtime health (independent Chrome/Edge); fall back to WP1 statuses.
  const phaseGOverview = browserMonitoringOverviewLabel(diagnostics?.browser_monitoring);
  const browserSummary = phaseGOverview
    ? {
      label: phaseGOverview,
      attention: ['STALE', 'DEGRADED', 'ERROR', 'NOT_RUNNING'].includes(
        String(diagnostics?.browser_monitoring?.pipeline_status || ''),
      )
        || ['STALE', 'DEGRADED', 'ERROR', 'NOT_RUNNING'].includes(
          String(diagnostics?.browser_monitoring?.chrome?.status || ''),
        )
        || ['STALE', 'DEGRADED', 'ERROR', 'NOT_RUNNING'].includes(
          String(diagnostics?.browser_monitoring?.edge?.status || ''),
        ),
      level: browserMonitoringHealthLevel(diagnostics?.browser_monitoring?.pipeline_status),
    }
    : diagnostics?.browser_extension?.chrome_status || diagnostics?.browser_extension?.edge_status
      ? {
        label: `Chrome: ${diagnostics.browser_extension.chrome_status || 'UNKNOWN'} · Edge: ${diagnostics.browser_extension.edge_status || 'UNKNOWN'}`,
        attention: ['STALE', 'OUTDATED'].includes(diagnostics.browser_extension.chrome_status)
          || ['STALE', 'OUTDATED'].includes(diagnostics.browser_extension.edge_status),
        level: 'info' as const,
      }
      : null;

  const captureMutation = useMutation({
    mutationFn: (enabled: boolean) => apiClient.devices.updateTracking(deviceId, enabled),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['monitoringDevicesInventory'] });
      queryClient.invalidateQueries({ queryKey: ['deviceDiagnosticsBrief', deviceId] });
    },
  });

  if (inventoryLoading && !device) {
    return <CircularProgress size={24} />;
  }

  if (!device && !diagnostics) {
    return <Alert severity="warning">Computer not found in inventory.</Alert>;
  }

  const employeeName = device?.employee?.name
    || diagnostics?.employee?.name
    || (diagnostics?.employee ? `${diagnostics.employee.firstName || ''} ${diagnostics.employee.lastName || ''}`.trim() : null)
    || 'Unassigned';
  const campaign = device?.campaign || diagnostics?.assignment?.current_campaign || '—';
  const heartbeat = device?.last_heartbeat_label
    || diagnostics?.device?.last_heartbeat_label
    || 'Unknown';
  const activityCaptureOn = device?.is_tracked !== false;

  return (
    <Box>
      <Card variant="outlined" sx={{ mb: 2 }}>
        <CardContent sx={{ py: 1.5, '&:last-child': { pb: 1.5 } }}>
          <Box display="flex" alignItems="center" justifyContent="space-between" gap={2} flexWrap="wrap">
            <Box>
              <Typography variant="subtitle2">Activity capture</Typography>
              <Typography variant="caption" color="text.secondary">
                When on, apps / web / activity are recorded for this computer while Windows is signed in (TEKS Sign In and Clock In not required). Web sites also need the browser extension. Default is on.
              </Typography>
            </Box>
            <FormControlLabel
              control={(
                <Switch
                  checked={activityCaptureOn}
                  disabled={!device || captureMutation.isPending}
                  onChange={(_, checked) => captureMutation.mutate(checked)}
                />
              )}
              label={activityCaptureOn ? 'On' : 'Off'}
            />
          </Box>
        </CardContent>
      </Card>

      <Grid container spacing={2}>
        <Grid item xs={12} md={6} lg={3}>
          <SummaryCard title="Workforce">
            <Row label="Employee" value={employeeName} />
            <Row label="Campaign" value={campaign} />
            <Row label="Clock Status" value={device?.clock_status || diagnostics?.work_state?.clock_status || '—'} />
            <Row label="Work State" value={device?.work_state_label || diagnostics?.work_state?.work_state || '—'} />
            <Row label="TEKS Auth" value={device?.teks_authorization || '—'} />
          </SummaryCard>
        </Grid>
        <Grid item xs={12} md={6} lg={3}>
          <SummaryCard title="Agent Health">
            <Row label="Health" value={<Chip size="small" color={diagnosticsHealthColor(String(device?.health_level || '').toLowerCase())} label={device?.agent_health || '—'} />} />
            <Row label="Version" value={device?.agent_version || diagnostics?.agent?.installed_version || '—'} />
            <Row label="Service" value={device?.service_status || diagnostics?.service?.status || '—'} />
            <Row label="Companion" value={device?.companion_status || diagnostics?.companion?.status || '—'} />
            <Row label="Queue" value={device?.pending_queue_count ?? diagnostics?.connectivity?.pending_local_queue_count ?? '—'} />
            <Row label="Heartbeat" value={heartbeat} />
          </SummaryCard>
        </Grid>
        <Grid item xs={12} md={6} lg={3}>
          <SummaryCard title="Windows">
            <Row label="Windows User" value={device?.windows_user || diagnostics?.device?.windows_username || '—'} />
            <Row label="OS" value={device?.os_version || diagnostics?.device?.os_version || '—'} />
            <Row label="Activity Capture" value={device?.is_tracked !== false ? 'On' : 'Off'} />
            <Row label="Registered" value={device?.registered_at ? new Date(device.registered_at).toLocaleString() : '—'} />
            <Row label="Last Seen" value={device?.last_seen ? new Date(device.last_seen).toLocaleString() : '—'} />
          </SummaryCard>
        </Grid>
        <Grid item xs={12} md={6} lg={3}>
          <SummaryCard title="Monitoring">
            <Row label="Application" value={device?.current_application || diagnostics?.activity?.last_application || '—'} />
            <Row label="Domain" value={device?.current_domain || '—'} />
            <Row label="Idle" value={device?.current_idle_seconds != null ? `${device.current_idle_seconds}s` : '—'} />
            <Row
              label="Browser Ext."
              value={
                <Typography
                  variant="body2"
                  color={browserSummary ? (browserSummary.attention ? 'warning.main' : 'text.primary') : (device?.browser_extension_connected ? 'text.primary' : 'warning.main')}
                  sx={{ cursor: 'pointer' }}
                  onClick={() => navigate(`/workforce-monitoring/computers/${deviceId}?tab=diagnostics`)}
                  aria-label={browserSummary?.label || device?.browser_extension_status || 'Browser extension status unknown'}
                >
                  {browserSummary?.label || device?.browser_extension_status || '—'}
                </Typography>
              }
            />
            <Row label="Activity" value={device?.current_activity || '—'} />
          </SummaryCard>
        </Grid>
        <Grid item xs={12}>
          <SummaryCard title="Alerts">
            {(device?.alerts || []).length === 0 ? (
              <Typography variant="body2" color="text.secondary">No active warnings.</Typography>
            ) : (
              (device.alerts || []).map((alert: any) => (
                <Alert
                  key={`${alert.type}-${alert.message}`}
                  severity={alert.severity === 'critical' ? 'error' : 'warning'}
                  icon={<WarningAmber fontSize="inherit" />}
                  sx={{ mb: 1 }}
                  action={
                    <Button size="small" onClick={() => navigate(`/workforce-monitoring/computers/${deviceId}?tab=diagnostics`)}>
                      Diagnostics
                    </Button>
                  }
                >
                  {alert.message}
                </Alert>
              ))
            )}
            {diagnosticsLoading && (
              <Typography variant="caption" color="text.secondary">Loading diagnostics details…</Typography>
            )}
          </SummaryCard>
        </Grid>
      </Grid>
    </Box>
  );
};

export default ComputerOverviewPanel;
